import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, ClipboardPaste, CloudUpload, Check } from "lucide-react";

const ITEM_HEADERS = ["sku", "quantity", "cny_unit_price", "rm_unit_price", "supplier_name", "supplier_source", "supplier_tracking_code"];
function parseItemsTSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const first = lines[0].split(/\t|,/).map(s => s.trim().toLowerCase());
  const headerLike = ITEM_HEADERS.some(h => first.includes(h));
  const cols = headerLike ? first : ITEM_HEADERS;
  const idx = (name) => cols.indexOf(name);
  return lines.slice(headerLike ? 1 : 0).map(l => {
    const p = l.split(/\t|,/).map(s => s.trim());
    return {
      sku: p[idx("sku")] || p[0] || "",
      quantity: parseInt(p[idx("quantity")] || p[1] || "0", 10),
      cny_unit_price: parseFloat(p[idx("cny_unit_price")] || p[2] || "0"),
      rm_unit_price: (p[idx("rm_unit_price")] || p[3]) ? parseFloat(p[idx("rm_unit_price")] || p[3]) : null,
      supplier_name: p[idx("supplier_name")] || "",
      supplier_source: p[idx("supplier_source")] || "",
      supplier_tracking_code: p[idx("supplier_tracking_code")] || "",
    };
  });
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [skus, setSkus] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState({ sku_id: "", quantity: 1, cny_unit_price: 0, rm_unit_price: null, shipping_fee_cn_cny: 0, adjustment_cny: 0, supplier_tracking_code: "", supplier_id: null });
  const [meta, setMeta] = useState({});
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const pastePreview = pasteText ? parseItemsTSV(pasteText) : [];

  const load = async () => {
    const [i, s, sup] = await Promise.all([api.get(`/invoices/${id}`), api.get("/skus"), api.get("/suppliers")]);
    setInv(i.data); setMeta({
      invoice_no: i.data.invoice_no || "", supplier: i.data.supplier || "",
      purchase_date: i.data.purchase_date, notes: i.data.notes || "",
      rm_exchange_rate: i.data.rm_exchange_rate ?? null,
    });
    setSkus(s.data);
    setSuppliers(sup.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleCheck = async (it) => {
    try {
      await api.patch(`/invoice-items/${it.id}/arrival-check`, { needs_arrival_check: !it.needs_arrival_check });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addItem = async () => {
    try {
      const payload = {
        sku_id: item.sku_id, quantity: parseInt(item.quantity, 10),
        cny_unit_price: parseFloat(item.cny_unit_price),
        rm_unit_price: item.rm_unit_price ? parseFloat(item.rm_unit_price) : null,
        shipping_fee_cn_cny: parseFloat(item.shipping_fee_cn_cny) || 0,
        adjustment_cny: parseFloat(item.adjustment_cny) || 0,
        supplier_tracking_code: item.supplier_tracking_code || "",
        supplier_id: item.supplier_id || null,
      };
      await api.post(`/invoices/${id}/items`, payload);
      toast.success("Item added");
      setOpen(false); setItem({ sku_id: "", quantity: 1, cny_unit_price: 0, rm_unit_price: null, shipping_fee_cn_cny: 0, adjustment_cny: 0, supplier_tracking_code: "", supplier_id: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSupplierTrack = async (itemId, code) => {
    try {
      await api.patch(`/invoice-items/${itemId}/supplier-tracking`, { supplier_tracking_code: code });
      toast.success("Supplier tracking saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const syncBukku = async () => {
    const t = toast.loading("Syncing to Bukku…");
    try {
      const { data } = await api.post(`/bukku/sync/invoice/${id}`);
      toast.success(`Bukku sync OK · ref ${data.ref}`, { id: t });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bukku sync failed", { id: t }); }
  };

  const buildInvoicePayload = (over = {}) => ({
    invoice_no: inv.invoice_no || "", supplier: inv.supplier || "",
    purchase_date: inv.purchase_date, notes: inv.notes || "",
    rm_exchange_rate: inv.rm_exchange_rate ?? null,
    billing_company_id: inv.billing_company_id || null,
    rounding_adjustment_rm: inv.rounding_adjustment_rm ?? 0,
    final_amount_rm: inv.final_amount_rm ?? null,
    bukku_synced: !!inv.bukku_synced, bukku_ref: inv.bukku_ref || "",
    ...over,
  });

  const saveRounding = async (value) => {
    try {
      await api.put(`/invoices/${id}`, buildInvoicePayload({ rounding_adjustment_rm: parseFloat(value) || 0 }));
      toast.success("Rounding saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveFinalRm = async (value) => {
    const v = value === "" || value === null ? null : parseFloat(value);
    try {
      await api.put(`/invoices/${id}`, buildInvoicePayload({ final_amount_rm: (v === null || isNaN(v)) ? null : v }));
      toast.success("Final RM total saved · costs averaged down"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeItem = async (iid) => {
    if (!window.confirm("Remove this line item?")) return;
    await api.delete(`/invoice-items/${iid}`); load();
  };

  const saveMeta = async () => {
    try {
      await api.put(`/invoices/${id}`, buildInvoicePayload({
        invoice_no: meta.invoice_no, supplier: meta.supplier,
        purchase_date: meta.purchase_date, notes: meta.notes,
        rm_exchange_rate: meta.rm_exchange_rate ? parseFloat(meta.rm_exchange_rate) : null,
      }));
      toast.success("Invoice updated"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkImport = async () => {
    if (!pastePreview.length) return toast.error("No rows to import");
    try {
      const { data } = await api.post(`/invoices/${id}/items/bulk`, { rows: pastePreview });
      toast.success(`Added ${data.created.length} line items${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors.length) data.errors.slice(0, 3).forEach(e => toast.warning(`Row ${e.row} (${e.sku}): ${e.error}`));
      setPasteOpen(false); setPasteText(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!inv) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <Link to="/invoices" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4 mr-1.5" />Back to invoices
      </Link>
      {(inv.invoice_no || inv.supplier) && (
        <Link to={`/inbound?q=${encodeURIComponent(inv.invoice_no || inv.supplier)}`}
          className="inline-flex items-center text-sm text-sky-600 hover:text-sky-800 mb-4 ml-4" data-testid="invoice-view-inbound">
          View Inbound Status <ArrowLeft className="w-4 h-4 ml-1.5 rotate-180" />
        </Link>
      )}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="caption">Invoice</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">
            {inv.invoice_no || <span className="text-amber-600">— pending invoice no —</span>}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{inv.supplier} · {inv.purchase_date}</p>
        </div>
        {user?.role === "admin" && (
          <div className="text-right">
            <div className="caption">CNY total</div>
            <div className="font-heading text-2xl font-bold cny-badge">¥ {inv.total_cny?.toFixed(2)}</div>
            {inv.total_rm_raw > 0 && <div className="text-xs text-slate-500 mt-1">Computed RM {inv.total_rm_raw.toFixed(2)}</div>}
            {inv.total_rm > 0 && <div className="rm-badge text-lg" data-testid="invoice-total-rm">RM {inv.total_rm.toFixed(2)}</div>}
            {inv.billing_company && <div className="text-xs text-slate-500 mt-1">Bukku vendor · <b>{inv.billing_company.name}</b> <span className="text-slate-400">(2nd tier)</span></div>}

            <div className="mt-3 flex items-center justify-end gap-2">
              <div className="text-xs text-slate-500">Final RM Total</div>
              <Input defaultValue={inv.final_amount_rm ?? ""} type="number" step="0.01" placeholder="actual invoice RM"
                data-testid="invoice-final-rm-input" className="h-8 w-28 text-right"
                onBlur={e => { const nv = e.target.value === "" ? null : parseFloat(e.target.value); if (nv !== (inv.final_amount_rm ?? null)) saveFinalRm(e.target.value); }} />
            </div>
            {inv.final_amount_rm != null && inv.rm_auto_adjustment != null && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="invoice-rm-auto-adj">
                Auto RM adj (average-down): <b className={inv.rm_auto_adjustment < 0 ? "text-rose-600" : "text-emerald-600"}>RM {inv.rm_auto_adjustment.toFixed(2)}</b>
              </div>
            )}
            <div className="mt-2 flex items-center justify-end gap-2">
              <div className="text-xs text-slate-400">Rounding adj. (CNY side)</div>
              <Input defaultValue={inv.rounding_adjustment_rm ?? 0} type="number" step="0.01"
                data-testid="invoice-rounding-input" className="h-8 w-24 text-right"
                onBlur={e => { if (parseFloat(e.target.value) !== (inv.rounding_adjustment_rm ?? 0)) saveRounding(e.target.value); }} />
            </div>

            {!inv.bukku_synced ? (
              <div className="mt-3">
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={syncBukku}
                  disabled={inv.final_amount_rm == null} data-testid="invoice-sync-bukku">
                  <CloudUpload className="w-4 h-4 mr-1.5" />Sync to Bukku
                </Button>
                {inv.final_amount_rm == null && <div className="text-[11px] text-amber-600 mt-1">Enter Final RM Total to enable sync</div>}
              </div>
            ) : (
              <div className="mt-3 text-xs text-emerald-600 flex items-center justify-end gap-1"><Check className="w-3 h-3" />Bukku ref {inv.bukku_ref}</div>
            )}
          </div>
        )}
      </div>

      {user?.role === "admin" && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 mb-6 grid md:grid-cols-5 gap-3 items-end">
          <div><Label>Invoice No</Label><Input data-testid="invoice-meta-no" value={meta.invoice_no} onChange={e=>setMeta({...meta, invoice_no:e.target.value})} placeholder="Fill later" /></div>
          <div><Label>Supplier</Label><Input value={meta.supplier} onChange={e=>setMeta({...meta, supplier:e.target.value})} /></div>
          <div><Label>Date</Label><Input type="date" value={meta.purchase_date} onChange={e=>setMeta({...meta, purchase_date:e.target.value})} /></div>
          <div><Label>RM Rate</Label><Input type="number" step="0.0001" value={meta.rm_exchange_rate ?? ""} onChange={e=>setMeta({...meta, rm_exchange_rate:e.target.value})} placeholder="0.65" /></div>
          <Button data-testid="invoice-meta-save" onClick={saveMeta} className="bg-slate-900"><Save className="w-4 h-4 mr-1.5" />Save</Button>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-bold">Line items</h2>
        {user?.role === "admin" && (
          <div className="flex gap-2">
          <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="items-paste-button">
                <ClipboardPaste className="w-4 h-4 mr-1.5" />Paste from Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>Bulk add line items from Excel</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Columns (in order): <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">sku · quantity · cny_unit_price · rm_unit_price</span>. First row can be a header. SKU must exist in the master sheet.</p>
                <Textarea data-testid="items-paste-textarea" rows={8} value={pasteText}
                  onChange={e=>setPasteText(e.target.value)}
                  placeholder={"sku\tquantity\tcny_unit_price\trm_unit_price\nSKU-001\t120\t8.50\t5.20"} />
                {pastePreview.length > 0 && (
                  <div className="border border-slate-200 rounded max-h-64 overflow-auto">
                    <table className="excel-table w-full text-xs">
                      <thead><tr><th>SKU</th><th>Qty</th><th>CNY/u</th><th>RM/u</th></tr></thead>
                      <tbody>
                        {pastePreview.slice(0, 50).map((r, i) => (
                          <tr key={i}><td className="font-mono">{r.sku}</td><td>{r.quantity}</td><td className="cny-badge">¥{r.cny_unit_price}</td><td>{r.rm_unit_price ? <span className="rm-badge">RM{r.rm_unit_price}</span> : "—"}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setPasteOpen(false)}>Cancel</Button>
                <Button onClick={bulkImport} data-testid="items-paste-import-button" className="bg-slate-900" disabled={!pastePreview.length}>
                  Import {pastePreview.length || ""} rows
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-line-item-button" className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />Add item</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add line item</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>SKU *</Label>
                  <Select value={item.sku_id} onValueChange={v=>setItem({...item, sku_id:v})}>
                    <SelectTrigger data-testid="line-item-sku-select"><SelectValue placeholder="Select SKU…" /></SelectTrigger>
                    <SelectContent>
                      {skus.map(s => <SelectItem key={s.id} value={s.id}>{s.sku} · {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Qty *</Label><Input type="number" data-testid="line-item-qty" value={item.quantity} onChange={e=>setItem({...item, quantity:e.target.value})} /></div>
                  <div><Label>CNY / unit *</Label><Input type="number" step="0.01" data-testid="line-item-cny" value={item.cny_unit_price} onChange={e=>setItem({...item, cny_unit_price:e.target.value})} placeholder="Base price" /></div>
                  <div><Label>RM / unit (opt override)</Label><Input type="number" step="0.01" data-testid="line-item-rm" value={item.rm_unit_price ?? ""} onChange={e=>setItem({...item, rm_unit_price:e.target.value})} placeholder="auto from rate" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Shipping in China (CNY / unit)</Label><Input type="number" step="0.01" data-testid="line-item-cn-ship" value={item.shipping_fee_cn_cny} onChange={e=>setItem({...item, shipping_fee_cn_cny:e.target.value})} placeholder="within-China courier" /></div>
                  <div><Label>Adjustment (CNY / unit)</Label><Input type="number" step="0.01" data-testid="line-item-adjust" value={item.adjustment_cny} onChange={e=>setItem({...item, adjustment_cny:e.target.value})} placeholder="rebate / surcharge" /></div>
                </div>
                <div><Label>Supplier tracking code (optional)</Label>
                  <Input data-testid="line-item-supplier-track" value={item.supplier_tracking_code || ""}
                    onChange={e=>setItem({...item, supplier_tracking_code:e.target.value})}
                    placeholder="Supplier → China warehouse. Leave empty for direct-to-warehouse." /></div>
                <div><Label>Supplier profile (optional)</Label>
                  <Select value={item.supplier_id || "none"} onValueChange={v=>setItem({...item, supplier_id: v === "none" ? null : v})}>
                    <SelectTrigger data-testid="line-item-supplier-select"><SelectValue placeholder="Pick a supplier…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No profile —</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name || s.chat_1688_name || s.wechat_username} · {s.source}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={addItem} data-testid="line-item-save" className="bg-slate-900">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full">
          <thead><tr>
            <th>SKU</th><th>Item</th><th>Rack</th><th>Supplier</th><th>Qty</th><th title="Quantity Needs Arrival Check">Chk</th>
            {user?.role === "admin" && <><th>CNY/u</th><th>CN Ship</th><th>Adj.</th><th>Total CNY/u</th><th>CNY total</th><th>RM/u</th><th>RM total</th></>}
            <th>Supplier Track</th><th>WH Track</th><th>Status</th>
            {user?.role === "admin" && <th></th>}
          </tr></thead>
          <tbody>
            {inv.items?.map(it => (
              <tr key={it.id}>
                <td className="font-mono font-semibold">{it.sku}</td>
                <td className="whitespace-nowrap">{it.item_name}</td>
                <td className="font-mono text-xs">{it.rack_code || "—"}</td>
                <td>
                  {it.supplier_name ? (
                    <div className="text-xs">
                      <div className="font-medium">{it.supplier_name}</div>
                      {it.supplier_source && <span className={`text-[10px] px-1.5 py-0.5 rounded ${it.supplier_source === "1688" ? "bg-orange-100 text-orange-700" : it.supplier_source === "wechat" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{it.supplier_source}</span>}
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td>{it.quantity}</td>
                <td className="text-center">
                  {user?.role === "admin" ? (
                    <input type="checkbox" data-testid={`item-check-${it.id}`} checked={!!it.needs_arrival_check}
                      onChange={() => toggleCheck(it)} title="Quantity Needs Arrival Check" className="align-middle cursor-pointer" />
                  ) : (it.needs_arrival_check ? <span title="Needs arrival check" className="inline-block w-2 h-2 rounded-full bg-amber-500" /> : <span className="text-slate-300">—</span>)}
                </td>
                {user?.role === "admin" && (<>
                  <td className="cny-badge">¥{it.cny_unit_price}</td>
                  <td className="cny-badge text-xs">{it.shipping_fee_cn_cny ? `¥${it.shipping_fee_cn_cny}` : "—"}</td>
                  <td className="cny-badge text-xs">{it.adjustment_cny ? `¥${it.adjustment_cny}` : "—"}</td>
                  <td className="cny-badge font-semibold">¥{it.total_cny_per_unit}</td>
                  <td className="cny-badge font-bold">¥{it.cny_total}</td>
                  <td>{it.rm_unit_price_computed ? <span className="rm-badge">RM{it.rm_unit_price_computed}</span> : <span className="text-slate-400">—</span>}</td>
                  <td>{it.rm_total ? <span className="rm-badge font-semibold">RM{it.rm_total}</span> : <span className="text-slate-400">—</span>}</td>
                </>)}
                <td>
                  {user?.role === "admin" ? (
                    <Input defaultValue={it.supplier_tracking_code || ""} data-testid={`supplier-track-${it.id}`}
                      className="h-8 w-36 font-mono text-xs" placeholder="—"
                      onBlur={e => { if (e.target.value !== (it.supplier_tracking_code || "")) saveSupplierTrack(it.id, e.target.value); }} />
                  ) : (<span className="font-mono text-xs">{it.supplier_tracking_code || "—"}</span>)}
                </td>
                <td className="font-mono text-xs text-slate-500">{it.shipment_id ? "grouped" : "—"}</td>
                <td><span className={`px-2 py-0.5 rounded text-xs font-medium status-${it.arrival_status}`}>{it.arrival_status}</span></td>
                {user?.role === "admin" && (
                  <td className="text-right"><Button variant="ghost" size="sm" onClick={()=>removeItem(it.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></td>
                )}
              </tr>
            ))}
            {inv.items?.length === 0 && (
              <tr><td colSpan={16} className="text-center py-12 text-slate-400">No items yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
